'use client';

import { useCalendarStore } from '@/stores/calendar-store';
import { canUnlockContent } from '@/lib/auth-context';
import { uiFb } from '@/lib/i18n';
import type { ReactNode } from 'react';

interface PremiumGateProps {
  /** Content always visible (free rows) */
  children: ReactNode;
  /** Number of locked items behind the gate */
  lockedCount: number;
  /** Label for what's locked, e.g. "countries" or "events" */
  lockedLabel?: string;
  /** Placeholder content rendered blurred when not premium */
  lockedPlaceholder?: ReactNode;
  /** CTA link target */
  ctaHref?: string;
}

export function PremiumGate({
  children,
  lockedCount,
  lockedLabel = 'countries',
  lockedPlaceholder,
  ctaHref = '/countries/id',
}: PremiumGateProps) {
  const userRole = useCalendarStore((s) => s.userRole);
  const lang = useCalendarStore((s) => s.lang);

  if (canUnlockContent(userRole) || lockedCount <= 0) {
    return <>{children}</>;
  }

  return (
    <>
      {children}
      <div className="relative mt-4">
        {/* Blurred placeholder rows */}
        <div className="select-none overflow-hidden rounded-xl" aria-hidden="true">
          <div className="pointer-events-none blur-[6px]">
            {lockedPlaceholder || <DefaultPlaceholder count={Math.min(lockedCount, 3)} />}
          </div>
        </div>

        {/* CTA overlay */}
        <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-surface-card/60 backdrop-blur-sm">
          <div className="text-center">
            <p className="mb-3 text-sm font-medium text-text-primary">
              +{lockedCount} {uiFb('ui.more_available', 'more', lang)} {lockedLabel} {uiFb('ui.available', 'available', lang)}
            </p>
            <a
              href={ctaHref}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-5 py-2.5 text-sm font-semibold text-text-on-brand shadow-sm transition-colors hover:bg-brand/90"
            >
              {uiFb('ui.unlock_all', 'Unlock all', lang)} {lockedLabel} &rarr;
            </a>
          </div>
        </div>
      </div>
    </>
  );
}

function DefaultPlaceholder({ count }: { count: number }) {
  const lang = useCalendarStore((s) => s.lang);
  const placeholders = [
    { title: uiFb('ui.seasonal_event', 'Seasonal event', lang), date: 'Jan 15 – Feb 28', region: 'Country A' },
    { title: uiFb('ui.type.public_holiday', 'Public Holiday', lang), date: 'Mar 1', region: 'Country B' },
    { title: uiFb('ui.cultural_celebration', 'Cultural celebration', lang), date: 'Apr 10 – Apr 12', region: 'Country C' },
  ];

  return (
    <div className="space-y-2 p-4">
      {placeholders.slice(0, count).map((p, i) => (
        <div
          key={i}
          className="flex items-center gap-4 content-card p-3"
        >
          <div className="h-2.5 w-2.5 rounded-full bg-text-dim/30" />
          <div className="flex-1">
            <p className="text-sm font-medium text-text-dim">{p.title}</p>
            <p className="text-xs text-text-dim/60">{p.region} &middot; {p.date}</p>
          </div>
        </div>
      ))}
    </div>
  );
}
